import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren,
} from "@angular/core";
import { Overlay, ScrollStrategy } from "@angular/cdk/overlay";
import {
  DropdownDirective,
  DropdownSelectMapOption,
} from "@bitwarden/web-vault/app/directives/dropdown.directive";
import { ClickOutsideReason } from "@bitwarden/angular/directives/click-outside.directive";
import { View } from "@bitwarden/web-vault/app/models/enum/view.enum";
import { CONNECTED_OVERLAY_POSITIONS_BELOW } from "@bitwarden/web-vault/app/shared/utils/overlay-positions";

// rename them maybe,
// default is the button style,
// mat is mat-alike, our current design for mat-autocomplete
export type PanelStyleTypes = "default" | "mat";
export type panelRadiusTypes = "default" | "rounded" | "xl";
export type DropdownSelectInputOptions = string[] | DropdownSelectMapOption[];

@Component({
  selector: "app-dropdown-select",
  templateUrl: "./dropdown-select.component.html",
})
export class DropdownSelectComponent extends DropdownDirective {
  private cdr = inject(ChangeDetectorRef);
  private elRef = inject(ElementRef);
  private overlay = inject(Overlay);

  private static idCounter = 0;
  // Unique per instance — this component can appear many times on one page (unlike
  // accounts-filter/plugins), so aria-controls needs an id that can't collide.
  protected readonly ddPanelId = `dropdown-select-panel-${DropdownSelectComponent.idCounter++}`;

  @ViewChild("ddTrigger") private ddTrigger?: ElementRef<HTMLElement>;
  // Both panel variants (inline, CDK-detached) share this template ref name — only one is
  // ever in the DOM at a time (gated by detachPanel), so ViewChild resolves whichever renders.
  @ViewChild("ddPanel") private ddPanel?: ElementRef<HTMLElement>;
  // Same sharing rationale as ddPanel — only the rendered variant's options ever populate this.
  @ViewChildren("ddOption") private ddOptions?: QueryList<ElementRef<HTMLElement>>;

  // Roving tabindex (APG Listbox pattern, #4657 follow-up): index into mappedOptions of the
  // option that currently owns the listbox's single Tab stop. -1 while closed or when every
  // option is disabled (nothing to focus).
  protected activeOptionIndex = -1;

  // todo: add these 2 to the other components when needed
  @Input() isRequired: boolean = false;
  @Input() isEnabled: boolean = true;
  /**
   * Opt-in: hold the page still (ScrollLockDirective) while the panel is open. For hosts that
   * would otherwise react to a page scroll by moving or hiding the open list, like the Views
   * filter collapsing on scroll-down. Off by default: this component is shared across the app.
   */
  @Input() lockPageScroll: boolean = false;
  @Input() panelStyle: PanelStyleTypes = "default";
  @Input() panelRadius: panelRadiusTypes = "default";
  @Input() customShadowClass: string = "";
  @Input() showGradientBorder: boolean = true;
  /** Compact variant: ~24px tall, raised neumorphic button, small text. Used for inline rows. */
  @Input() compact: boolean = false;
  @Input() controlHeightClass: string | null = null;
  /** Overrides the trigger's down-arrow icon size (tailwind spacing units). Null = default. */
  @Input() arrowSize: number | null = null;
  @Input() joinPanelOnExpand: boolean = false;
  /** Center the selected-value text in the trigger instead of left-aligning it. */
  @Input() centerSelected: boolean = false;
  /**
   * Opaque flat panel + plain grey row highlight, instead of the default translucent glass panel
   * and the past/today/future tinted-glow selected-row treatment (which implies a timeline concept
   * that doesn't apply to a plain filter menu).
   */
  @Input() flat: boolean = false;

  //
  @Input() label: string;
  @Input() labelClass: string;
  @Input() placeholder: string;
  @Input() options: DropdownSelectInputOptions = [];
  @Input() disabledOptions: string[] = [];
  @Input() testLabel: string;
  @Input() selectedOption: string;
  @Output() optionSelected = new EventEmitter<string>();
  // maybe move to directive
  protected readonly View = View;
  @Input() currentView: View = View.TODAY;

  // Opt-in: render the options panel in a CDK overlay so it escapes clipping/scrolling
  // ancestors (e.g. a modal-body). Default keeps the inline absolute panel.
  @Input() detachPanel: boolean = false;
  protected readonly overlayPositions = CONNECTED_OVERLAY_POSITIONS_BELOW;
  // autoClose: true closes the panel once the trigger scrolls out of view rather than letting
  // cdkConnectedOverlayPush clamp it to the viewport edge, floating detached over unrelated
  // content (#4536/#4535 follow-up).
  protected readonly overlayScrollStrategy: ScrollStrategy =
    this.overlay.scrollStrategies.reposition({ autoClose: true });

  // Extra classes appended to the options panel (e.g. a min-width or a custom gap from the
  // trigger). When non-empty, this also suppresses the default margin-top/radius so there's no
  // ambiguity between two same-specificity classes both setting the same property.
  @Input() panelClass: string = "";

  // In detached mode the panel is portaled out of the wrapper, so the appClickOutside
  // directive's "outside-click" would false-positive on option clicks — let the overlay's own
  // outside-click (onOverlayOutsideClick) handle mouse dismissal instead. Escape has no such
  // false-positive (it doesn't depend on DOM containment), and the CDK overlay has no built-in
  // Escape handling of its own, so detached panels still need it here (#4511 follow-up).
  override onClickOutside(reason: ClickOutsideReason = "outside-click") {
    if (!this.isExpanded || (this.detachPanel && reason !== "escape")) {
      return;
    }
    super.onClickOutside();
    // Only Escape has no other sensible focus target — restore to the trigger. A genuine
    // outside click already moved focus wherever the user clicked (mousedown moves focus
    // before this mouseup-driven handler runs); forcibly refocusing the trigger here would
    // yank focus away from the element the user just clicked into. Even for Escape, only do
    // this if focus is currently *within this component* — otherwise a stray Escape while this
    // panel is open in the background (the user is really interacting with something else,
    // e.g. an unrelated dialog) would yank focus away from wherever it legitimately is. Checked
    // against both elRef and ddPanel: in detachPanel mode the panel is CDK-portaled outside
    // this component's own DOM subtree, so elRef alone wouldn't see focus that's inside it.
    if (
      reason === "escape" &&
      (this.elRef.nativeElement.contains(document.activeElement) ||
        this.ddPanel?.nativeElement.contains(document.activeElement))
    ) {
      this.ddTrigger?.nativeElement.focus();
    }
  }

  onOverlayOutsideClick(event: MouseEvent, trigger: HTMLElement) {
    if (!trigger.contains(event.target as Node)) {
      this.isExpanded = false;
    }
  }

  override toggleExpand() {
    const willOpen = !this.isExpanded;
    super.toggleExpand();
    if (willOpen) {
      // Roving tabindex (APG Listbox pattern): land on the selected option if there is one
      // (and it's enabled), else the first enabled option, so re-opening resumes where the
      // user left off instead of always restarting at the top.
      this.activeOptionIndex = this.computeInitialActiveIndex();
      // detectChanges() forces the *ngIf-gated panel into the DOM synchronously, so the
      // ViewChild(ren) are resolvable and .focus() lands immediately.
      this.cdr.detectChanges();
      if (!this.focusActiveOption()) {
        // Every option is disabled (or the list is empty) — nothing to land a roving tab
        // stop on, so fall back to the panel container itself rather than focusing nothing.
        this.ddPanel?.nativeElement.focus({ preventScroll: true });
      }
    } else {
      this.ddTrigger?.nativeElement.focus();
    }
  }

  /**
   * Up/Down move the roving tab stop between enabled options; Home/End jump to the first/last
   * enabled one. Clamps rather than wraps at either end (simpler to reason about and to test;
   * APG permits either). Enter/Space stay on each option's own binding — the event target is
   * already the focused option, so nothing here needs to change for those.
   */
  protected onListboxKeydown(event: KeyboardEvent): void {
    if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) {
      return;
    }
    event.preventDefault();
    const focusable = this.focusableOptionIndices();
    if (focusable.length === 0) {
      return;
    }
    let targetIndex: number | undefined;
    switch (event.key) {
      case "ArrowDown":
        targetIndex = focusable.find((i) => i > this.activeOptionIndex);
        break;
      case "ArrowUp":
        targetIndex = [...focusable].reverse().find((i) => i < this.activeOptionIndex);
        break;
      case "Home":
        targetIndex = focusable[0];
        break;
      case "End":
        targetIndex = focusable[focusable.length - 1];
        break;
    }
    if (targetIndex === undefined) {
      // Already at the first/last enabled option for that direction — nothing to do.
      return;
    }
    this.activeOptionIndex = targetIndex;
    const target = this.ddOptions?.get(targetIndex)?.nativeElement;
    // preventScroll keeps the page itself from jumping, but that also suppresses the panel's
    // own tw-overflow-y-auto scroll container — without this, the roving tab stop moves past
    // the visible ~5 rows with no visible focus ring to show for it.
    target?.focus({ preventScroll: true });
    target?.scrollIntoView?.({ block: "nearest" });
  }

  /**
   * Single Tab stop (APG Listbox pattern) means Tab moves focus straight out of the component —
   * unlike Escape/outside-click, that's not routed through appClickOutside at all, so the panel
   * must close itself here or it's left open in the background. Not guarded on `isExpanded`
   * being false the way onClickOutside is: focusout only ever fires from a real DOM blur inside
   * this component's own subtree, so there's no equivalent "global listener firing for an
   * unrelated reason" risk to guard against. relatedTarget is checked against both elRef and
   * ddPanel for the same reason onClickOutside does — in detachPanel mode the panel is
   * CDK-portaled outside this component's own DOM subtree.
   */
  protected onListboxFocusOut(event: FocusEvent): void {
    const next = event.relatedTarget as Node | null;
    if (!next) {
      // relatedTarget is null on a window/tab blur (e.g. alt-tab), not just a focus move outside
      // this component — closing here would leave the dropdown shut when the window regains
      // focus, even though nothing inside it was actually left.
      return;
    }
    if (this.elRef.nativeElement.contains(next) || this.ddPanel?.nativeElement.contains(next)) {
      return;
    }
    this.isExpanded = false;
  }

  private computeInitialActiveIndex(): number {
    const selectedIndex = this.mappedOptions.findIndex(
      (option) => option.realValue === this.selectedOption && !this.isDisabled(option.realValue),
    );
    if (selectedIndex !== -1) {
      return selectedIndex;
    }
    return this.mappedOptions.findIndex((option) => !this.isDisabled(option.realValue));
  }

  private focusableOptionIndices(): number[] {
    return this.mappedOptions.flatMap((option, index) =>
      this.isDisabled(option.realValue) ? [] : [index],
    );
  }

  /** Returns whether an option was actually found and focused. */
  private focusActiveOption(): boolean {
    if (this.activeOptionIndex < 0) {
      return false;
    }
    const target = this.ddOptions?.get(this.activeOptionIndex)?.nativeElement;
    // See onListboxKeydown: preventScroll also suppresses the panel's own scroll container, so
    // a selection near the bottom would otherwise land off-screen when the dropdown opens.
    target?.focus({ preventScroll: true });
    target?.scrollIntoView?.({ block: "nearest" });
    return !!target;
  }

  override selectOption(option: string) {
    const wasExpanded = this.isExpanded;
    super.selectOption(option);
    if (wasExpanded && !this.isExpanded) {
      this.ddTrigger?.nativeElement.focus();
    }
  }

  override ngOnChanges(changes: SimpleChanges): void {
    super.ngOnChanges(changes);
    if (changes.isEnabled && !changes.isEnabled.firstChange) {
      if (changes.isEnabled.currentValue) {
        this.isTouched = false;
      } else {
        this.isExpanded = false;
      }
    }
  }

  override isInvalid(): boolean {
    return this.isEnabled && super.isInvalid();
  }
}
